import { useEffect, useRef, useState } from "react";

/**
 * Charts, hand-built in SVG.
 *
 * No charting library. What this dashboard needs is one stacked column chart
 * and a sparkline; a general-purpose library is 60-90KB gzipped to draw two
 * shapes, and every one of them then has to be fought to get the mark specs
 * below. Written directly, the whole file is smaller than the library's
 * tree-shaken core and every pixel is deliberate.
 *
 * Mark specs, applied throughout:
 *   - columns capped at 24px, never filling their slot — the leftover is air
 *   - 4px rounded cap on the top of a stack, square at the baseline
 *   - a 2px gap in the surface colour between stacked segments; never a stroke
 *   - hairline, solid, recessive gridlines — never dashed
 *   - text wears text tokens, never a series colour
 */

const AXIS_LABEL = "fill-[var(--chart-axis)] text-[10px]";

export interface Series {
  readonly key: string;
  readonly label: string;
  readonly color: string;
}

export interface StackPoint {
  readonly bucket: string;
  readonly values: Readonly<Record<string, number>>;
}

/**
 * Round a maximum up to something a human would choose for an axis.
 *
 * `1, 2, 5 × 10^n`. Without it the top gridline reads "7" and the reader has to
 * do arithmetic to place anything between the ticks.
 */
function niceMax(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalised = value / magnitude;
  const step = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10;
  return step * magnitude;
}

function ticksFor(max: number, count = 4): number[] {
  const step = max / count;
  return Array.from({ length: count + 1 }, (_, i) => Math.round(i * step));
}

export function StackedColumns({
  points,
  series,
  bucket,
  minHeight = 180,
}: {
  points: readonly StackPoint[];
  series: readonly Series[];
  bucket: "hour" | "day";
  /** Floor for the plot area; it grows to fill whatever height the card gives it. */
  minHeight?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const [width, setWidth] = useState(640);
  const [height, setHeight] = useState(minHeight);
  const box = useRef<HTMLDivElement>(null);

  // Measured rather than assumed: an SVG viewBox that does not match its
  // container makes every label either microscopic or clipped.
  useEffect(() => {
    const element = box.current;
    if (!element) return;
    // Both dimensions are measured. Height matters because the card stretches
    // to match the column beside it, and a fixed-height chart in a stretched
    // card leaves a dead band underneath that reads as a rendering fault.
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setWidth(entry.contentRect.width);
      setHeight(Math.max(minHeight, entry.contentRect.height));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const PAD = { top: 12, right: 8, bottom: 22, left: 30 };
  const plotW = Math.max(80, width - PAD.left - PAD.right);
  const plotH = height - PAD.top - PAD.bottom;

  const totals = points.map((p) => series.reduce((sum, s) => sum + (p.values[s.key] ?? 0), 0));
  const max = niceMax(Math.max(1, ...totals));
  const ticks = ticksFor(max);

  const slot = plotW / Math.max(1, points.length);
  const barW = Math.min(24, Math.max(3, slot - 4));

  return (
    <div ref={box} className="relative h-full w-full">
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`Submissions and decisions per ${bucket}`}
        onMouseLeave={() => setHover(null)}
      >
        {/* Gridlines and their labels. Solid hairlines a single step off the
            surface: present enough to read a value against, quiet enough to
            disappear when you are looking at the data. */}
        {ticks.map((t) => {
          const y = PAD.top + plotH - (t / max) * plotH;
          return (
            <g key={t}>
              <line
                x1={PAD.left}
                x2={width - PAD.right}
                y1={y}
                y2={y}
                stroke="var(--chart-grid)"
                strokeWidth={1}
                shapeRendering="crispEdges"
              />
              <text x={PAD.left - 6} y={y + 3} textAnchor="end" className={AXIS_LABEL}>
                {t}
              </text>
            </g>
          );
        })}

        {points.map((point, i) => {
          const x = PAD.left + i * slot + (slot - barW) / 2;
          const total = totals[i] ?? 0;

          // The whole slot is the hit target, not the bar: a 3px column in a
          // 40px slot is impossible to hover, and a tooltip you cannot open is
          // the same as no tooltip.
          const hit = (
            <rect
              key={`hit-${point.bucket}`}
              x={PAD.left + i * slot}
              y={PAD.top}
              width={slot}
              height={plotH}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
            />
          );

          if (total === 0) {
            return (
              <g key={point.bucket}>
                {/* An empty bucket gets a baseline tick rather than nothing, so
                    a quiet day reads as quiet instead of as missing data. */}
                <rect
                  x={x}
                  y={PAD.top + plotH - 2}
                  width={barW}
                  height={2}
                  rx={1}
                  fill="var(--chart-pending)"
                />
                {hit}
              </g>
            );
          }

          let cursor = PAD.top + plotH;
          const segments = series
            .map((s) => {
              const value = point.values[s.key] ?? 0;
              if (value <= 0) return null;
              const raw = (value / max) * plotH;
              // The 2px surface gap between touching segments. Subtracted from
              // the segment, never drawn as a stroke around it.
              const h = Math.max(2, raw - 2);
              cursor -= raw;
              return { key: s.key, color: s.color, y: cursor, h, value, label: s.label };
            })
            .filter((v): v is NonNullable<typeof v> => v !== null);

          return (
            <g key={point.bucket} className={hover === i ? "" : ""}>
              {segments.map((seg, index) => (
                <rect
                  key={seg.key}
                  x={x}
                  y={seg.y}
                  width={barW}
                  height={seg.h}
                  // Only the topmost segment gets the rounded cap; the rest are
                  // square so the stack reads as one column, not as beads.
                  rx={index === segments.length - 1 ? 4 : 0}
                  fill={seg.color}
                  className="bar-grow"
                  style={{
                    animationDelay: `${Math.min(i * 18, 320)}ms`,
                    opacity: hover === null || hover === i ? 1 : 0.42,
                    transition: "opacity 140ms ease-out",
                  }}
                />
              ))}
              {hit}
            </g>
          );
        })}

        {/* The baseline is the one rule that is not recessive: every column is
            measured from it, so it has to be unambiguous. */}
        <line
          x1={PAD.left}
          x2={width - PAD.right}
          y1={PAD.top + plotH}
          y2={PAD.top + plotH}
          stroke="var(--border-strong)"
          strokeWidth={1}
          shapeRendering="crispEdges"
        />

        {/* Selective x labels — first, last, and a few between. A label under
            every column at 14 buckets is a grey smear. */}
        {points.map((point, i) => {
          const every = Math.max(1, Math.ceil(points.length / 6));
          if (i % every !== 0 && i !== points.length - 1) return null;
          return (
            <text
              key={`x-${point.bucket}`}
              x={PAD.left + i * slot + slot / 2}
              y={height - 6}
              textAnchor="middle"
              className={AXIS_LABEL}
            >
              {formatBucket(point.bucket, bucket)}
            </text>
          );
        })}
      </svg>

      {hover !== null && points[hover] && (
        <Tooltip
          point={points[hover]}
          series={series}
          bucket={bucket}
          // Kept inside the container: a tooltip that runs off the right edge
          // of the card is worse than none.
          left={Math.min(Math.max(PAD.left + hover * slot + slot / 2, 70), width - 70)}
        />
      )}
    </div>
  );
}

function Tooltip({
  point,
  series,
  bucket,
  left,
}: {
  point: StackPoint;
  series: readonly Series[];
  bucket: "hour" | "day";
  left: number;
}) {
  const total = series.reduce((sum, s) => sum + (point.values[s.key] ?? 0), 0);
  return (
    <div
      className="pointer-events-none absolute top-0 z-10 -translate-x-1/2 rounded-[10px] border border-[var(--border-strong)] bg-[var(--surface-2)] px-3 py-2 shadow-[var(--elevation-pop)]"
      style={{ left }}
    >
      <div className="text-[11px] font-medium text-[var(--foreground)]">
        {formatBucket(point.bucket, bucket, true)}
      </div>
      <div className="mt-1.5 grid gap-1">
        {series.map((s) => (
          <div key={s.key} className="flex items-center gap-2 text-[11px] whitespace-nowrap">
            {/* Identity comes from the swatch beside the text, never from
                colouring the text — a mid-chroma fill is illegible as type. */}
            <span className="h-2 w-2 shrink-0 rounded-[2px]" style={{ background: s.color }} />
            <span className="text-[var(--muted)]">{s.label}</span>
            <span className="tabular ml-auto pl-3 text-[var(--foreground)]">
              {point.values[s.key] ?? 0}
            </span>
          </div>
        ))}
        {total > 0 && (
          <div className="mt-0.5 flex items-center gap-2 border-t border-[var(--border)] pt-1 text-[11px]">
            <span className="text-[var(--faint)]">Total</span>
            <span className="tabular ml-auto text-[var(--foreground)]">{total}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function formatBucket(iso: string, bucket: "hour" | "day", long = false): string {
  const date = new Date(iso);
  if (bucket === "hour") {
    return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  }
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(long ? { weekday: "short" } : {}),
  });
}

/**
 * A sparkline for a stat tile.
 *
 * No axes, no labels, no tooltip — it is a shape, not a chart. Its job is to
 * say "rising", "flat" or "spiky" in the corner of a number, and anything more
 * would compete with the number it belongs to.
 */
export function Sparkline({
  values,
  color,
  width = 92,
  height = 26,
}: {
  values: readonly number[];
  color: string;
  width?: number;
  height?: number;
}) {
  if (values.length < 2) return <div style={{ width, height }} aria-hidden="true" />;

  const max = Math.max(1, ...values);
  const step = width / (values.length - 1);
  const points = values.map((v, i) => [i * step, height - (v / max) * (height - 3) - 1.5] as const);

  const line = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${line} L${width},${height} L0,${height} Z`;
  const last = points[points.length - 1]!;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      {/* A ~10% wash under the line, never a saturated block. */}
      <path d={area} fill={color} opacity={0.12} />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {/* The end dot carries a surface ring so it stays legible where it sits
          on the line, and marks "this is now". */}
      <circle cx={last[0]} cy={last[1]} r={3} fill={color} stroke="var(--surface-1)" strokeWidth={2} />
    </svg>
  );
}

/**
 * A figure that animates to its new value.
 *
 * Not decoration: on a dashboard that updates itself, a number silently
 * changing between glances is a change you miss. Easing to the new value over
 * a few hundred milliseconds is the cheapest way to make it register.
 */
export function AnimatedNumber({ value, className = "" }: { value: number; className?: string }) {
  const [shown, setShown] = useState(value);
  const previous = useRef(value);
  const [bumped, setBumped] = useState(false);

  useEffect(() => {
    const from = previous.current;
    previous.current = value;
    if (from === value) return;

    setBumped(true);
    const bumpTimer = setTimeout(() => setBumped(false), 400);

    // A short count for a small delta, no count at all for a big jump — easing
    // through four hundred values is a slot machine, not information.
    if (Math.abs(value - from) > 40) {
      setShown(value);
      return () => clearTimeout(bumpTimer);
    }

    const started = performance.now();
    const DURATION = 420;
    let frame = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - started) / DURATION);
      // Cubic ease-out, matching the rest of the motion in the console.
      const eased = 1 - (1 - t) ** 3;
      setShown(Math.round(from + (value - from) * eased));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(bumpTimer);
    };
  }, [value]);

  return (
    <span className={`tabular inline-block ${bumped ? "animate-tick" : ""} ${className}`}>
      {shown.toLocaleString()}
    </span>
  );
}

/**
 * A live indicator that emits a ring when something actually happens.
 *
 * The steady pulse says "connected"; the ring says "an event just arrived".
 * Two different facts, and conflating them would mean the dashboard looks busy
 * on a quiet afternoon — which is the failure mode of every decorative
 * "activity" animation.
 */
export function LiveDot({ active, pinged }: { active: boolean; pinged: boolean }) {
  return (
    <span className="relative inline-flex h-2 w-2 shrink-0">
      {pinged && active && (
        <span
          className="animate-ping-ring absolute inset-0 rounded-full bg-[var(--brand)]"
          aria-hidden="true"
        />
      )}
      <span
        className={
          "relative inline-flex h-2 w-2 rounded-full " +
          (active ? "animate-pulse-dot bg-[var(--ok)]" : "bg-[var(--faint)]")
        }
      />
    </span>
  );
}
